export interface WeeklyReportItem {
  id: string;
  text: string;
  completed: boolean;
}

/**
 * Email attachment interface
 */
export interface EmailAttachment {
  /** Unique identifier for the attachment */
  id: string;
  /** Original filename */
  filename: string;
  /** MIME type (e.g., 'application/pdf', 'image/png') */
  mimeType: string;
  /** File size in bytes */
  size: number;
  /** Base64-encoded file content */
  content: string;
}

/**
 * Email delivery status
 */
export interface EmailDeliveryStatus {
  /** Gmail message ID */
  messageId?: string;
  /** Thread ID */
  threadId?: string;
  /** When the email was sent */
  sentAt: string;
  /** Delivery status */
  status: "sent" | "failed" | "pending";
  /** Error message if failed */
  error?: string;
  /** Labels applied to the message */
  labels?: string[];
  /** Whether the email was verified to exist in Gmail */
  verified?: boolean;
  /** Warning message (non-fatal issues) */
  warning?: string;
}

/**
 * Report status types for Drafts 2.0
 * Replaces sentByEmail boolean with explicit status field
 */
export type ReportStatus = "draft" | "pending" | "sent" | "failed";

export interface WeeklyReport {
  id: string;
  date: string;
  dateRange?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  recipients?: string[];
  sentByEmail?: boolean; // DEPRECATED: use status instead
  subject: string;
  sentAt?: string; // ISO string, set when status becomes 'sent'
  createdBy?: string; // User email who created the report
  clientName?: string; // Client name associated with this report
  last_week_tasks?: { value: string; dependency?: string }[];
  Unplanned?: { value: string; dependency?: string }[];
  blockers?: { value: string; dependency?: string }[];
  next_week_tasks?: { value: string; dependency?: string }[];
  further_beyond?: { value: string; dependency?: string }[];
  notes?: string;
  greeting?: string;
  signature?: string;
  content?: {
    done: WeeklyReportItem[];
    Unplanned: WeeklyReportItem[];
    nextWeek: WeeklyReportItem[];
    furtherBeyond: WeeklyReportItem[];
  };
  /** File attachments */
  attachments?: EmailAttachment[];
  /** Email delivery status (populated after sending) */
  deliveryStatus?: EmailDeliveryStatus;

  // === Drafts 2.0 Fields ===
  /** Report status: draft (unsent), pending (saved), sent, or failed */
  status?: ReportStatus;
  /** When report was created (ISO string) */
  createdAt?: string;
  /** When report was last modified (ISO string, updated on autosave) */
  updatedAt?: string;
  /** Stable report identifier for versioning */
  reportId?: string;
  /** Version number for conflict resolution */
  version?: number;
  /** Email of user who last saved this report */
  lastSavedBy?: string;
  /** Autosave metadata */
  draftMetadata?: {
    autoSaveIntervalMs?: number;
    lastAutoSaveAt?: string;
    hasUnsavedChanges?: boolean;
    localDraftId?: string;
  };
  /** Derived status for API responses (backward compatibility) */
  statusResolved?: ReportStatus;
}

// ============================================
// Template Types - Enhanced
// ============================================

export type TemplateVisibility = "private" | "shared" | "organization";
export type TemplatePermission = "view" | "use" | "edit";

export interface SharedAccess {
  userId: string;
  permission: TemplatePermission;
  sharedAt: string;
  sharedBy: string;
}

export interface TemplateVersion {
  versionId: string;
  versionNumber: number;
  createdAt: string;
  createdBy: string;
  changeDescription?: string;
  changes: Record<string, unknown>;
  isPublished: boolean;
  isPinned?: boolean;
}

export interface VersionDiff {
  fromVersion: number;
  toVersion: number;
  changes: Array<{
    field: string;
    oldValue: unknown;
    newValue: unknown;
    type: "added" | "removed" | "modified";
  }>;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  to: string[];
  cc: string[];
  bcc: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  greeting?: string;
  signature?: string;
  isDefault?: boolean;

  // Phase 1: Categories & Tags
  categoryId?: string | null;
  tags?: string[];

  // Phase 2: Sharing
  visibility?: TemplateVisibility;
  ownerId?: string;
  sharedWith?: SharedAccess[];

  // Phase 3: Versioning
  currentVersion?: number;
  versions?: TemplateVersion[];
  draftVersion?: TemplateVersion | null;
}

// ============================================
// Category Types
// ============================================

export interface TemplateCategory {
  id: string;
  name: string;
  description: string;
  color: string;
  icon?: string;
  sortOrder: number;
  isSystem: boolean;
}

export interface TagStats {
  tag: string;
  count: number;
  lastUsed: string;
}

// ============================================
// API Request/Response Types
// ============================================

export interface ShareTemplateRequest {
  userEmails: string[];
  permission: TemplatePermission;
}

export interface TemplateFilterParams {
  category?: string;
  tags?: string[];
  visibility?: TemplateVisibility;
  search?: string;
}

// ============================================
// Default Categories
// ============================================

export const DEFAULT_CATEGORIES: TemplateCategory[] = [
  {
    id: "weekly-reports",
    name: "Weekly Reports",
    description: "Templates for weekly status reports",
    color: "#3B82F6",
    icon: "calendar",
    sortOrder: 1,
    isSystem: true,
  },
  {
    id: "project-updates",
    name: "Project Updates",
    description: "Templates for project status updates",
    color: "#10B981",
    icon: "briefcase",
    sortOrder: 2,
    isSystem: true,
  },
  {
    id: "status-reports",
    name: "Status Reports",
    description: "General status report templates",
    color: "#8B5CF6",
    icon: "file-text",
    sortOrder: 3,
    isSystem: true,
  },
  {
    id: "meeting-notes",
    name: "Meeting Notes",
    description: "Templates for meeting summaries",
    color: "#F59E0B",
    icon: "users",
    sortOrder: 4,
    isSystem: true,
  },
  {
    id: "client-communication",
    name: "Client Communication",
    description: "Templates for client-facing emails",
    color: "#EF4444",
    icon: "mail",
    sortOrder: 5,
    isSystem: true,
  },
  {
    id: "internal-updates",
    name: "Internal Updates",
    description: "Templates for internal team updates",
    color: "#06B6D4",
    icon: "home",
    sortOrder: 6,
    isSystem: true,
  },
  {
    id: "custom",
    name: "Custom/Other",
    description: "Custom templates and miscellaneous",
    color: "#6B7280",
    icon: "folder",
    sortOrder: 99,
    isSystem: true,
  },
];

// ============================================
// API Token Types
// ============================================

/**
 * Available scopes for API tokens
 * Currently only reports:write is supported for security.
 */
export type ApiTokenScope = "reports:write";

/**
 * Token info returned in list (without the actual token)
 */
export interface ApiTokenInfo {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt?: string;
  scopes: ApiTokenScope[];
  isExpired: boolean;
}

/**
 * Request to create a new API token
 */
export interface CreateApiTokenRequest {
  name: string;
  expiresInDays: number;
  scopes: ApiTokenScope[];
}

/**
 * Response when creating a token (includes the actual token - shown only once!)
 */
export interface CreateApiTokenResponse {
  token: string;
  id: string;
  name: string;
  tokenPrefix: string;
  expiresAt: string;
  scopes: ApiTokenScope[];
}

/**
 * Default expiration options
 */
export const TOKEN_EXPIRATION_OPTIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "Never", days: 3650 }, // ~10 years
];

/**
 * Default scopes for new tokens
 */
export const DEFAULT_TOKEN_SCOPES: ApiTokenScope[] = ["reports:write"];

/**
 * Available scopes with descriptions
 */
export const AVAILABLE_SCOPES: { scope: ApiTokenScope; description: string }[] =
  [{ scope: "reports:write", description: "Submit weekly reports via API" }];
